import 'simplebar/dist/simplebar.min.css';
import { IDisposable, inject, observable, watch } from 'aurelia';
import {
    ICurrentRoute,
    IRouteConfig,
    IRouterEvents,
    IRouteViewModel,
    RouteNode
} from '@aurelia/router-lite';
import { IWebsitePage } from '@chicksgroup/types';
import { IStore, fromState } from '@aurelia/state';

const Blogs = () => import('./pages/blogs/blogs');
const Blogpost = () => import('./pages/blogs/blogpost/blogpost');
const Author = () => import('./pages/blogs/author/author');
const UnknownRoute = () => import('./pages/unknown-route/unknown-route');

import { PAGE_NAMES } from './resources/constants';
import { AuthClient, AuthenticationHandler, Helper } from './resources/extensions';
import { WebsiteService } from './services/website-service';
import { ActionNames, IAppState, IMetadataState, UpdateMetaRobots, UpdateMetaTitle, UpdateOgUrl, UpdateRouterCurrentUrl } from './state';
import { IMetadataService } from './services/metadata-service';
import { SessionService } from './services/session-service';
import { User } from './services/models/user';
import { websiteShortCode } from './environment';
import { SimplebarOverride } from './resources/simplebar-override';
import { IpAddressService } from './services/ip-address-service';
import { SiteStyleService } from './services/site-style-service';
import { NameFromShortcodeValueConverter } from './resources/value-converters';
import { LanguageWebsiteService } from './services/language-website-service';
import { Language } from './state/models/ILanguage';

AuthClient.handleSSORedirect();

@inject(
    WebsiteService,
    IMetadataService,
    SessionService,
    IStore,
    IRouterEvents,
    ICurrentRoute,
    Helper,
    IpAddressService,
    SiteStyleService,
    NameFromShortcodeValueConverter,
    AuthenticationHandler,
    LanguageWebsiteService
)
export class App implements IRouteViewModel {
    user: User | null;
    pages: IWebsitePage[];
    @observable @fromState((state: IAppState) => state.metadata) metadata: IMetadataState;
    @fromState((state: IAppState) => state.router.currentUrl) currentUrl: string;
    navEndSub: IDisposable;
    navStartSub: IDisposable;
    websiteShortCode = websiteShortCode() ?? 'CG';
    languages: Language[] | null = null;
    currentLanguage: Language | undefined;

    constructor
    (
        private readonly websiteService: WebsiteService,
        private readonly metadataService: IMetadataService,
        private readonly sessionService: SessionService,
        private readonly store: IStore<IAppState, UpdateRouterCurrentUrl | UpdateOgUrl | UpdateMetaRobots | UpdateMetaTitle>,
        private readonly events: IRouterEvents,
        private readonly currentRoute: ICurrentRoute,
        private readonly helper: Helper,
        private readonly ipAddressService: IpAddressService,
        private readonly siteStyleService: SiteStyleService,
        private readonly nameFromShortcodeValueConverter: NameFromShortcodeValueConverter,
        private readonly authHandler: AuthenticationHandler,
        private readonly languageWebsiteService: LanguageWebsiteService
    ) {
        this.helper = helper;
        this.navStartSub = this.events.subscribe('au:router:navigation-start', async () => {
            const path = window.location.pathname;

            const hasUppercase = /[A-Z]/.test(path);
            const needsAmpReplace = path.includes('/category/') && path.includes('%26');

            if (hasUppercase || needsAmpReplace) {
                let newUrl = path;

                if (needsAmpReplace) {
                    newUrl = newUrl.replace(/%26/g, 'and');
                }

                if (hasUppercase) {
                    newUrl = newUrl.toLowerCase();
                }

                this.helper.addPrerenderMetaTagForRedirect(newUrl);
                window.location.href = newUrl;
                return;
            }
        });
        this.navEndSub = this.events.subscribe('au:router:navigation-end', () => {
            // need to set timeout as currentRoute not update imediatelly after navigation end event is published
            setTimeout(() => {
                this.store.dispatch({ type: ActionNames.UPDATE_ROUTER_CURRENT_URL, value: this.currentRoute.url });
                this.store.dispatch({ type: ActionNames.UPDATE_META_TITLE, value: this.currentRoute.title });
            }, 100);

            this.store.dispatch({ type: ActionNames.UPDATE_OG_URL, value: window.location.pathname === '/' ? window.location.origin : window.location.href.split('?')?.[0] });
            this.store.dispatch({ type: ActionNames.UPDATE_META_ROBOTS, value: this.helper.includesSome(window.location.href, ['?', '/page/', '404']) ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' });
            this.helper.saveWindowLocalStorageValue('meta_title', document.title ? document.title : this.getDefaultTitle());
        });
    }

    async binding() {
        this.siteStyleService.loadSiteStyle(websiteShortCode() ?? 'CG');
        if (!this.sessionService.geolocation) await this.ipAddressService.getIpData();
        await this.initialize();
    }

    async initialize() {
        await this.authHandler.handleSSORedirection();
        this.user = await this.sessionService.getProfile();
        if (this.user) await this.ipAddressService.postIp();
    }

    async attached() {
        this.helper.changeFavicon(websiteShortCode() ?? 'CG');
        const mainPageHost = document.getElementById('main-page-host') as HTMLElement;
        if (mainPageHost) {
            new SimplebarOverride(mainPageHost);
        }
    }

    dispose() {
        this.navEndSub.dispose();
        this.navStartSub.dispose();
    }

    public async getRouteConfig(_parentConfig: IRouteConfig | null, _routeNode: RouteNode | null): Promise<IRouteConfig> {
        const [_, pages] = await Promise.all([
            this.loadLanguageFromURL(),
            this.websiteService.getPagesByWebsiteShortcode()
        ]);
        this.pages = pages;
        const hreflangList = this.languages?.filter(lang => lang.language?.hrefLang).filter(lang => lang.language.hrefLang !== 'en').map(lang => {
            return {
                hreflang: lang.language.hrefLang,
                route: lang.language.hrefLang.split('-').join('').toLowerCase()
            };
        });

        const routes = [
            this.getRouteObject(PAGE_NAMES.UNKNOWN_ROUTE, UnknownRoute, '404'),
            this.getRouteObject(PAGE_NAMES.BLOG, Blogs, '', undefined, hreflangList),
            this.getRouteObject(PAGE_NAMES.BLOG, Blogpost, '', ':blog', hreflangList),
            this.getRouteObject(PAGE_NAMES.BLOG, Author, '', 'author/:alias', hreflangList)
        ];

        return {
            routes: routes.flat(),
            fallback: UnknownRoute,
        };
    }

    getRouteObject(name: string, component: IRouteViewModel, defaultPath?: string, childPath?: string, languages?: { hreflang: string; route: string; }[], auth?: boolean) {
        const routeName = this.pages[name]?.routeName;
        const routes: Array<{
            path: string;
            component: IRouteViewModel;
            title: string;
            data: {
                metaDescription: string | undefined;
                auth: boolean | undefined;
            };
        }> = [];

        routes.push({
            path: (defaultPath ?? routeName ?? Helper.toRoute(name)) + (childPath ? `/${childPath}` : ''),
            component: component,
            title: this.getTitleForPage(name)?.includes(this.nameFromShortcodeValueConverter.toView(this.websiteShortCode)) ? this.getTitleForPage(name) : `${this.getTitleForPage(name)} - ${this.nameFromShortcodeValueConverter.toView(this.websiteShortCode)}`,
            data: {
                metaDescription: this.getMetaDescriptionForPage(name),
                auth: auth
            }
        });

        if (languages) {
            languages.forEach(lang => {
                routes.push({
                    path: `/${lang.hreflang}` + ((defaultPath ?? routeName ?? Helper.toRoute(name)) + (childPath ? `/${childPath}` : '')),
                    component: component,
                    title: this.getTitleForPage(name)?.includes(this.nameFromShortcodeValueConverter.toView(this.websiteShortCode)) ? this.getTitleForPage(name) : `${this.getTitleForPage(name)} - ${this.nameFromShortcodeValueConverter.toView(this.websiteShortCode)}`,
                    data: {
                        metaDescription: this.getMetaDescriptionForPage(name),
                        auth: auth
                    }
                });
            });
        }

        return routes;
    }

    get showHeaderAndFooter() {
        return this.pages && !['sign-up', 'sign-in'].includes(this.currentUrl);
    }

    private getTitleForPage(name: string) {
        const title = this.pages?.find(x => x.name === name)?.title;
        return title ? title : '';
    }

    private getMetaDescriptionForPage(name: string) {
        return this.pages?.find(x => x.name === name)?.metaDescription;
    }

    @watch('metadata')
    handleMetadataChanged(metadata: IMetadataState) {
        this.metadataService.updateMetaDescription(metadata.ogDescription);
        this.metadataService.updateMetaTitle(metadata.ogTitle);
        this.metadataService.updateMetaImage(metadata.ogImage);
        this.metadataService.updateMetaUrl(metadata.ogUrl);
        this.metadataService.updateMetaType(metadata.ogType);
        this.metadataService.updateMetaSiteName(metadata.ogSiteName);
        this.metadataService.updateMetaTwitterCard(metadata.ogTwitterCard);
        this.metadataService.updateMetaTwitterCreator(metadata.ogTwitterCreator);
        this.metadataService.updateMetaTwitterSite(metadata.ogTwitterSite);
        this.metadataService.updateMetaPagination(metadata.pagination, this.currentUrl);
        this.metadataService.updateMetaRobots(metadata.robots);
        this.metadataService.updateMetaThemeColor(metadata.themeColor);
        window.prerenderReady = true;
    }

    get scrollBarClass() {
        return `scrollbar-${websiteShortCode()?.toLowerCase()}`;
    }

    getDefaultTitle = () => {
        const titleMap = [
            { keyword: 'chicksx', title: 'ChicksX' },
            { keyword: 'chicksgold', title: 'ChicksGold' },
            { keyword: 'acckings', title: 'AccKings' },
            { keyword: 'aribagold', title: 'AribaGold' },
            { keyword: 'gamertotal', title: 'GamerTotal' },
        ];

        const matched = titleMap.find(({ keyword }) => location.hostname.includes(keyword));
        return matched ? matched.title : 'Divica Sales';
    };

    async loadLanguageFromURL() {
        this.languages = await this.languageWebsiteService.getByWebsite();

        const hreflangList = this.languages?.filter(lang => lang.language.hrefLang).map(lang => lang.language.hrefLang);

        const parsedUrl = new URL(window.location.href);
        const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);

        const languageFromURL = pathSegments.length > 0 ? pathSegments[0] : '';

        const currentLanguage = await this.sessionService.getLanguage();
        const languageToSet = languageFromURL && hreflangList?.includes(languageFromURL) ?
            this.languages?.find(e => e.language.hrefLang === languageFromURL) :
            this.languages?.find(e => e.language.hrefLang === 'en');
        const currentLanguageHrefLang = currentLanguage?.language ? currentLanguage.language.hrefLang : currentLanguage?.hrefLang;

        if (currentLanguage && languageToSet && currentLanguageHrefLang !== languageToSet.language.hrefLang)
        {
            this.sessionService.saveLanguage(languageToSet);
            this.currentLanguage = languageToSet;
            return;
        }

        this.currentLanguage = currentLanguage;
    }
}
